import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import Tile from "components/Tile";
import TileBody from "components/TileBody";
import * as utils from "common/utils";
import Alert from "components/Alert";
import RadioGroupPack from "components/RadioGroupPack";
import numeral from "numeral";
import Watermark from "components/Watermark";
import * as domain from "common/domain";

export default function EnterpriseMonthResults({ budget, collapsed }) {
    const refData = useSelector((state) => state.app.refData);
    const [options, setOptions] = useState([
        { value: "MetabolicRequirements", text: "ME Requirements (MJ ME)", checked: true },
        { value: "DryMatterConsumed", text: "DM Intake (kg DM)", checked: false },
        { value: "ExcretaN", text: "Excreta N (kg N)", checked: false },
    ]);
    const [monthlySourceType, setMonthlySourceType] = useState("MetabolicRequirements");
    const showPercentageOfMERequirements = monthlySourceType === "MetabolicRequirements";

    useEffect(() => setMonthlySourceType(options.find((opt) => opt.checked).value), [options]);

    const sourceTypeChanged = (e) => setOptions((prevState) => prevState.map((opt) => ({ ...opt, checked: e.target.id === `monthlySourceType_${opt.value}` })));

    const { currentResults = {} } = budget;
    const { animalResults = {} } = currentResults;
    const { enterprises = [] } = budget;
    const { enterpriseTypes = [] } = refData;
    let totals = Array(13).fill(0);

    return (
        <Tile title="Monthly requirements and intake" open={!collapsed} secondary collapsible className="u-page-break">
            <TileBody>
                {(!animalResults || Object.keys(animalResults).length === 0) && <Alert type="info" text="There are currently no animal results for this analysis. Please correct any errors to run the report." />}
                {animalResults && Object.keys(animalResults).length > 0 && (
                    <>
                        <div className="Table u-mt-md">
                            <Watermark />
                            <table>
                                <thead>
                                    <tr>
                                        <th colSpan="14">
                                            <RadioGroupPack id="monthlySourceType" name="monthlySourceType" meta={{ nrf: true }} options={options} inline={true} onChange={sourceTypeChanged} />
                                        </th>
                                    </tr>
                                    <tr>
                                        <th data-width="20">Enterprise</th>
                                        {domain.farmYear.map((m) => (
                                            <th key={m} className="u-textCenter">
                                                {m.substring(0, 3)}
                                            </th>
                                        ))}
                                        <th className="u-textCenter">Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {enterprises
                                        .filter((e) => Object.keys(animalResults).includes(e.id))
                                        .map((e, i) => {
                                            const animalResult = animalResults[e.id];
                                            const monthlyResults = animalResult.monthlyResults[monthlySourceType];
                                            const sources = animalResult.sources;
                                            const name = utils.valueToText(enterpriseTypes, e.type);
                                            let rowTotal = 0;
                                            return (
                                                <tr key={i} id={utils.strToId(name)}>
                                                    <td>{name}</td>
                                                    {domain.farmYear.map((m, j) => {
                                                        const value = monthlyResults.results[m] ? monthlyResults.results[m] : 0;
                                                        totals[j] += value;
                                                        totals[12] += value;
                                                        rowTotal += value;

                                                        const monthlySources = Object.keys(sources).reduce((total, srcKey) => {
                                                            total += sources[srcKey].produced.find((p) => p.month === m).value;
                                                            return total;
                                                        }, 0);
                                                        const sourceAsPercentageOfRequirement = showPercentageOfMERequirements && value > 0 && monthlySources > 0 ? utils.round((monthlySources / value) * 100, 1) : undefined;

                                                        return (
                                                            <td key={m} className={`u-textCenter ${sourceAsPercentageOfRequirement > 120 ? "Alert--error" : ""}`}>
                                                                <div>{numeral(value).format("0,0")}</div>
                                                                {sourceAsPercentageOfRequirement > 0 && (
                                                                    <div className={`small ${sourceAsPercentageOfRequirement > 120 ? "u-textError" : ""}`}>
                                                                        {sourceAsPercentageOfRequirement}% <sup>*</sup>
                                                                    </div>
                                                                )}
                                                            </td>
                                                        );
                                                    })}
                                                    <td className="u-textRight">{numeral(rowTotal).format("0,0")}</td>
                                                </tr>
                                            );
                                        })}
                                </tbody>
                                <tfoot>
                                    {
                                        <tr key={"totals_monthlyresults"} id={"totals_monthlyResults"}>
                                            <th>Totals</th>
                                            {totals.map((t, i) => (
                                                <th className="u-textRight" key={"totals_monthlyresults_month_" + i} id={"totals_monthlyresults_month_" + i}>
                                                    <h4 className="u-mt-0">{numeral(t).format("0,0")}</h4>
                                                </th>
                                            ))}
                                        </tr>
                                    }
                                </tfoot>
                            </table>
                        </div>
                        {showPercentageOfMERequirements && (
                            <div>
                                <sub>* Percentage of ME requirements from all available sources. Anything over 120% of ME requirements is considered an overfeed error.</sub>
                            </div>
                        )}
                    </>
                )}
            </TileBody>
        </Tile>
    );
}
